import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ProcurementItem } from '@/types/database/procurement-items';
import {
  useUpdateItemWarehouseData,
  useUpdateItemQualityData,
} from '@/hooks/useAdminProcurementAdjustment';
import { Loader2 } from 'lucide-react';

interface ItemAdjustmentSectionProps {
  items: ProcurementItem[];
  procurementId: string;
}

interface ItemFormData {
  received_quantity_kg: number;
  compensation_kg: number;
  damage_kg: number;
}

export function ItemAdjustmentSection({
  items,
  procurementId,
}: ItemAdjustmentSectionProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [itemData, setItemData] = useState<Record<string, ItemFormData>>(() => {
    const initial: Record<string, ItemFormData> = {};
    items.forEach((item) => {
      initial[item.id] = {
        received_quantity_kg: item.item_warehouse?.received_quantity_kg ?? item.quantity,
        compensation_kg: item.item_quality?.compensation_kg ?? 0,
        damage_kg: item.item_quality?.damage_kg ?? 0,
      };
    });
    return initial;
  });
  const [savingItems, setSavingItems] = useState<Set<string>>(new Set());

  const updateItemWarehouse = useUpdateItemWarehouseData();
  const updateItemQuality = useUpdateItemQualityData();

  const toggleItem = (itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleItemChange = (
    itemId: string,
    field: keyof ItemFormData,
    value: string
  ) => {
    const numValue = parseFloat(value) || 0;
    setItemData((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: Math.max(0, numValue),
      },
    }));
  };

  const handleSaveItem = async (item: ProcurementItem) => {
    const data = itemData[item.id];
    if (!data) return;

    setSavingItems((prev) => new Set(prev).add(item.id));

    try {
      const netPayableKg = Math.max(0, data.received_quantity_kg - data.compensation_kg);

      // Update warehouse data
      await updateItemWarehouse.mutateAsync({
        procurementId,
        itemId: item.id,
        data: {
          received_quantity_kg: data.received_quantity_kg,
          net_payable_kg: netPayableKg,
        },
        reason: `Admin adjustment for item: ${item.product_name}`,
      });

      // Update quality data
      await updateItemQuality.mutateAsync({
        procurementId,
        itemId: item.id,
        data: {
          damage_kg: data.damage_kg,
          compensation_kg: data.compensation_kg,
        },
        reason: `Admin adjustment for item: ${item.product_name}`,
      });
    } finally {
      setSavingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  const calculateNetPayable = (itemId: string) => {
    const data = itemData[itemId];
    if (!data) return 0;
    return Math.max(0, data.received_quantity_kg - data.compensation_kg);
  };

  return (
    <div className="space-y-4 mt-6">
      <h3 className="font-medium flex items-center gap-2">
        <Package className="h-4 w-4" />
        Item-Level Adjustments
      </h3>
      <p className="text-sm text-muted-foreground">
        Adjust individual item quantities for multi-product procurements
      </p>

      <div className="space-y-2">
        {items.map((item) => (
          <Collapsible
            key={item.id}
            open={expandedItems.has(item.id)}
            onOpenChange={() => toggleItem(item.id)}
          >
            <div className="border rounded-lg">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between p-4 h-auto"
                >
                  <div className="flex items-center gap-2">
                    {expandedItems.has(item.id) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-medium">{item.product_name}</span>
                    <span className="text-muted-foreground">
                      ({item.quantity} kg ordered)
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Net: {calculateNetPayable(item.id).toFixed(2)} kg
                  </span>
                </Button>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Received (KG)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={itemData[item.id]?.received_quantity_kg || ''}
                        onChange={(e) =>
                          handleItemChange(item.id, 'received_quantity_kg', e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Damage (KG)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={itemData[item.id]?.damage_kg || ''}
                        onChange={(e) =>
                          handleItemChange(item.id, 'damage_kg', e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Compensation (KG)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={itemData[item.id]?.compensation_kg || ''}
                        onChange={(e) =>
                          handleItemChange(item.id, 'compensation_kg', e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Net Payable (KG)</Label>
                      <div className="h-10 px-3 py-2 bg-muted rounded-md flex items-center">
                        {calculateNetPayable(item.id).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => handleSaveItem(item)}
                    disabled={savingItems.has(item.id)}
                  >
                    {savingItems.has(item.id) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Item Changes
                  </Button>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        ))}
      </div>
    </div>
  );
}
